const SCRIPT_ID = 'youtube-iframe-api';
const SCRIPT_SRC = 'https://www.youtube.com/iframe_api';
const LOAD_TIMEOUT_MS = 10000;

export type YoutubeNamespace = {
  Player: new (...args: any[]) => unknown;
  PlayerState?: Record<string, number>;
  [key: string]: unknown;
};

let loadPromise: Promise<YoutubeNamespace> | null = null;

const hasWindow = typeof window !== 'undefined';

const getYoutubeNamespace = (): YoutubeNamespace | null => {
  if (!hasWindow) return null;
  const namespace = (window as typeof globalThis & { YT?: YoutubeNamespace }).YT;
  if (namespace && typeof namespace.Player === 'function') {
    return namespace;
  }
  return null;
};

const resolveWithNamespace = (
  resolve: (namespace: YoutubeNamespace) => void,
  cleanup: () => void
): boolean => {
  const namespace = getYoutubeNamespace();
  if (namespace) {
    cleanup();
    resolve(namespace);
    return true;
  }
  return false;
};

const assignReadyCallback = (handler: () => void) => {
  (window as typeof globalThis & { onYouTubeIframeAPIReady?: () => void }).onYouTubeIframeAPIReady =
    handler;
};

const clearReadyCallback = (handler: () => void) => {
  const scopedWindow = window as typeof globalThis & { onYouTubeIframeAPIReady?: () => void };
  if (scopedWindow.onYouTubeIframeAPIReady === handler) {
    delete scopedWindow.onYouTubeIframeAPIReady;
  }
};

export const loadYoutubeIframeAPI = (): Promise<YoutubeNamespace> => {
  if (!hasWindow || typeof document === 'undefined') {
    return Promise.reject(new Error('YouTube IFrame API can only load in a browser environment.'));
  }

  const namespace = getYoutubeNamespace();
  if (namespace) {
    return Promise.resolve(namespace);
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<YoutubeNamespace>((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    let script = existingScript;
    let timeoutId: number | undefined;

    const cleanup = () => {
      if (typeof timeoutId === 'number') {
        window.clearTimeout(timeoutId);
      }
      if (script) {
        script.removeEventListener('error', handleError);
      }
      clearReadyCallback(handleReady);
    };

    const handleReady = () => {
      resolveWithNamespace(resolve, cleanup);
    };

    const handleError = () => {
      cleanup();
      loadPromise = null;
      reject(new Error('Failed to load YouTube IFrame API.'));
    };

    assignReadyCallback(handleReady);

    timeoutId = window.setTimeout(() => {
      if (!resolveWithNamespace(resolve, cleanup)) {
        handleError();
      }
    }, LOAD_TIMEOUT_MS);

    if (script) {
      script.addEventListener('error', handleError, { once: true });
      if (resolveWithNamespace(resolve, cleanup)) {
        return;
      }
      return;
    }

    script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });

  return loadPromise;
};

export const resetYoutubeIframeApiLoader = () => {
  loadPromise = null;
  const scopedWindow = window as typeof globalThis & {
    YT?: YoutubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  };
  delete scopedWindow.onYouTubeIframeAPIReady;
  delete scopedWindow.YT;
  const script = document.getElementById(SCRIPT_ID);
  if (script && script.parentElement) {
    script.parentElement.removeChild(script);
  }
};

export const __TESTING_ONLY__ = {
  get loadPromise(): Promise<YoutubeNamespace> | null {
    return loadPromise;
  },
};
